/**
 * Shorthand date formatting for drawing-tool axis labels:
 * "Thu 18 Jun '26" (weekday abbrev · day · short month · apostrophe 2-digit
 * year), with an optional " HH:mm" suffix for intraday timestamps.
 *
 * Built from `Intl.DateTimeFormat` parts rather than a hand-rolled month/weekday
 * table so locale month names stay correct. `en-GB` gives day-before-month
 * ordering; we reassemble from `formatToParts` to drop en-GB's comma after the
 * weekday ("Thu, 18 Jun 26" → "Thu 18 Jun '26") and to prefix the apostrophe on
 * the year.
 *
 * The timezone is threaded through (from the overlay's `dateTimeFormat`) so the
 * pill date matches the crosshair for evening timestamps — using the browser
 * default here would shift the date by a day near midnight UTC.
 */
/**
 * True when `ts` is a daily-or-higher bar, detected by the backend's noon-UTC
 * normalization: `fetch_chart_bars` normalizes daily+ timestamps to noon UTC
 * (midnight + 12h), so `ts % 24h === 12h`. Intraday bars carry real intraday
 * times and (almost) never land exactly on noon UTC. Used to drop the time
 * suffix from a daily line label ("Thu 18 June 26", not "…08:00").
 */
export function isDailyNormalizedTimestamp(ts: number): boolean {
  const DAY_MS = 86_400_000
  const NOON_MS = 43_200_000
  return ((ts % DAY_MS) + DAY_MS) % DAY_MS === NOON_MS
}

export function formatShorthandDate(ts: number, timeZone?: string, includeTime = false): string {
  const dateFmt = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    timeZone,
  })
  const parts = dateFmt.formatToParts(ts)
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? ''
  // Apostrophe year ("'26"): Intl has no option for it, so prefix it here.
  let out = `${get('weekday')} ${get('day')} ${get('month')} '${get('year')}`

  if (includeTime) {
    const timeFmt = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    })
    out += ` ${timeFmt.format(ts)}`
  }
  return out
}

/** One formatter per timezone: the x-axis formats every tick on every draw. */
const dayBreakFormatters = new Map<string, Intl.DateTimeFormat>()

/**
 * Intraday x-axis day-break label: "Aug 25". klinecharts' default is "08-25",
 * which reads like the time 08:25 next to the HH:mm ticks. The timezone comes
 * from the chart's `dateTimeFormat` so the break lands on the exchange day.
 */
export function formatDayBreakLabel(ts: number, timeZone?: string): string {
  const key = timeZone ?? ''
  let fmt = dayBreakFormatters.get(key)
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone })
    dayBreakFormatters.set(key, fmt)
  }
  return fmt.format(ts)
}
